import { hexToRgba, colors, palette } from '@flex-design-system/fx-colors';
import { createStitches } from '@flex/stitches-react-patch';

const defaultIntensities = {
  default: {
    hover: 0.04,
    active: 0.08
  },
  weak: {
    hover: 0.02,
    active: 0.04
  },
  strong: {
    hover: 0.1,
    active: 0.2
  },
  none: {
    hover: 0,
    active: 0
  }
};
const MOBILE_BREAKPOINT = 600;
const SIZE_MAP = {
  xsmall: 24,
  small: 32,
  default: 40,
  large: 48,
  xlarge: 56
};
const {
  styled,
  css,
  globalCss,
  keyframes,
  getCssText,
  theme,
  createTheme,
  config,
  reset
} = createStitches({
  media: {
    "full-content": "(max-width: 1200px)",
    mobile: `(max-width: ${MOBILE_BREAKPOINT}px)`,
    motion: "(prefers-reduced-motion: no-preference)"
  },
  theme: {
    fontWeights: {},
    lineHeights: {},
    letterSpacings: {},
    sizes: Object.entries(SIZE_MAP).reduce((acc, [sizeKey, value]) => {
      return {
        ...acc,
        [sizeKey]: `${value}px`
      };
    }, {}),
    borderWidths: {},
    borderStyles: {},
    radii: {
      default: "8px",
      tag: "50px"
    },
    shadows: {
      active: "0 0 0 4px rgba(58,151,212,.3),0 0 0 1px rgba(33,36,41,.2),0 1px 1px 0 rgba(33,36,41,0),0 6px 12px 0 rgba(33,36,41,0)",
      pageRoot: "0px 0px 12px 0px rgba(0, 0, 0, 0.04), -1px 0px 0px 0px rgba(0, 0, 0, 0.04), 0px 0px 24px 0px rgba(0, 0, 0, 0.04)",
      pageAside: "inset 1px 0 0 rgba(0, 0, 0, 0.08)",
      button: "0px 2px 6px rgba(0, 0, 0, 0.02), inset 0px -1px 0px rgba(0, 0, 0, 0.06), inset 0px 0px 0px 1px rgba(0, 0, 0, 0.08)",
      tag: "inset 0px 0px 0px 1px rgba(0, 0, 0, 0.03)",
      tagOutline: "inset 0px 0px 0px 1px rgba(0, 0, 0, 0.12)",
      badge: "inset 0px 0px 0px 1px rgba(0, 0, 0, 0.04)",
      radioUnselect: "inset 0px 0px 0px 1px rgba(0, 0, 0, 0.16)",
      radioSelect: "inset 0px 0px 0px 1px rgba(0, 0, 0, 0.12)",
      checkboxUnselect: "inset 0px 0px 0px 1px rgba(0, 0, 0, 0.16)",
      checkboxSelect: "inset 0px 0px 0px 1px rgba(0, 0, 0, 0.12)",
      popper: "0px 0px 0px 1px rgba(0, 0, 0, 0.03), 0px 10px 14px rgba(0, 0, 0, 0.06), 0px 14px 32px rgba(0, 0, 0, 0.12)",
      inputSolid: "inset 0px 0px 0px 1px rgba(0, 0, 0, 0.04)",
      inputOutline: "inset 0px 0px 0px 1px rgba(0, 0, 0, 0.1)",
      inputFocus: "inset 0px 0px 0px 1px #09BB1B",
      inputError: "inset 0px 0px 0px 1px #F44712",
      popover: "0px 0px 0px 1px rgba(0, 0, 0, 0.03), 0px 14px 60px rgba(0, 0, 0, 0.15)",
      modal: "0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 24px 72px rgba(36, 42, 48, 0.22)",
      modalLeftAsideDivider: "inset -1px 0px 0px rgba(0, 0, 0, 0.08)",
      modalRightAsideDivider: "inset 1px 0px 0px rgba(0, 0, 0, 0.08)",
      tabActiveUnderline: "inset 0px -2px 0px #242A30",
      appShellRoot: "0px 0px 0px 1px rgba(0, 0, 0, 0.1)",
      tooltip: "0px 6px 24px rgba(0, 0, 0, 0.16), inset 0px -1px 0px rgba(0, 0, 0, 0.06), inset 0px 0px 0px 1px rgba(0, 0, 0, 0.12)",
      datePickerSelected: "inset 0px 0px 0px 1px rgba(0, 0, 0, 0.1)",
      dialog: "0px 0px 0px 1px rgba(0, 0, 0, 0.05), 0px 14px 60px rgba(0, 0, 0, 0.15)"
    },
    zIndices: {
      TOP_BAR: 999,
      SUB_NAV_BAR: 999,
      GNB: 1e3,
      /**
       * @deprecated v2.123.0 부터는 사용하지 않는 값입니다.
       * 이전 형상의 리모트 앱이 값을 참조하는 경우를 대비해 남겨둡니다.
       */
      LINEAR_MODAL: 1001,
      /**
       * 1. toast, tooltip 의 컴포넌트가 가장 위에 렌더링되도록 하는 z-index 값입니다.
       * 2. FE 내부에서 사용하는 devTool 인 flag, locale 에서 사용합니다.
       * 3. 헬프센터에서 사용하고 있는데, 헬프센터는 늘 최상단에 떠야하기 때문입니다.
       * 그 외의 사용하는 경우는 주의 부탁드립니다.
       *
       * TargetCardPopover 에서 사용되고 있는데 fds 이고 곧 지워질 값이라 크게 신경쓰지 않습니다.
       */
      TOP_OF_EVERYTHING: 1e4
    },
    transitions: {},
    colors: {
      ...colors,
      overlayLight: "rgba(240, 240, 240, 0.85)",
      overlayDark: hexToRgba("#242A30", 0.5)
    }
  },
  utils: {
    gradientText: (value) => {
      return {
        backgroundImage: palette.gradient[value],
        backgroundClip: "text",
        textFillColor: "transparent",
        "-webkit-background-clip": "text",
        "-webkit-text-fill-color": "transparent"
      };
    },
    size: (value) => ({
      width: value,
      height: value
    }),
    mx: (value) => ({ marginLeft: value, marginRight: value }),
    my: (value) => ({ marginTop: value, marginBottom: value }),
    px: (value) => ({
      paddingLeft: value,
      paddingRight: value
    }),
    py: (value) => ({
      paddingTop: value,
      paddingBottom: value
    }),
    clickable: (params) => {
      if (params === void 0) {
        return {};
      }
      const { intensity, options } = typeof params === "string" ? { intensity: params, options: {} } : params;
      if (intensity === "none") {
        return {
          ["&:before"]: {
            display: "none"
          }
        };
      }
      if (typeof intensity === "string" && !Object.keys(defaultIntensities).includes(intensity)) {
        return {};
      }
      const intensities = typeof intensity === "string" ? defaultIntensities[intensity] : intensity;
      const activeTransformStyle = options?.activeTransform !== void 0 ? {
        transition: "0s",
        transform: options.activeTransform
      } : {};
      const horizontalReverseOffset = options?.horizontalOffset;
      const horizontalOffsetStyle = horizontalReverseOffset !== void 0 ? {
        paddingLeft: `${horizontalReverseOffset}px`,
        paddingRight: `${horizontalReverseOffset}px`,
        marginLeft: `${ -1 * horizontalReverseOffset}px`,
        marginRight: `${ -1 * horizontalReverseOffset}px`
      } : {};
      const defaultBeforeStyle = {
        content: "",
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        backgroundColor: `${colors.grayDarker}`,
        borderRadius: "inherit",
        opacity: 0,
        transition: "0.15s",
        pointerEvents: "none"
      };
      return {
        position: "relative",
        cursor: "pointer",
        ...horizontalOffsetStyle,
        "&:before": options?.initialTransform === void 0 ? defaultBeforeStyle : {
          ...defaultBeforeStyle,
          transform: options.initialTransform
        },
        "&:hover": {
          "&:before": {
            transition: "opacity 0s",
            opacity: intensities.hover,
            transform: "none"
          }
        },
        "&:active": {
          "&:before": {
            transition: "opacity 0s",
            opacity: intensities.active
          },
          ...activeTransformStyle
        },
        /**
         * safari <= 15.4이하에서는 focus-visible이 유효하지 않아 focus로 style을 적용한다.
         * chrome에서도 focus 스타일을 적용할 경우 Button클릭 후 스타일이 사라지지 않는 현상이 있어 supports로 컨트롤
         * @supports selector는 Safari 14.1부터 지원되지만, known(won't fix) issue로 다룬다.
         */
        "@supports not selector(:focus-visible)": {
          "&:focus": {
            "&:before": {
              transition: "opacity 0s",
              opacity: intensities.active
            },
            ...activeTransformStyle
          }
        },
        "&:focus-visible": {
          "&:before": {
            transition: "opacity 0s",
            opacity: intensities.active
          },
          ...activeTransformStyle
        }
      };
    },
    hoverable: (params) => {
      if (params === void 0) {
        return {};
      }
      const { intensity, options } = typeof params === "string" ? { intensity: params, options: {} } : params;
      if (intensity === "none") {
        return {
          ["&:before"]: {
            display: "none"
          }
        };
      }
      if (typeof intensity === "string" && !Object.keys(defaultIntensities).includes(intensity)) {
        return {};
      }
      const intensities = typeof intensity === "string" ? defaultIntensities[intensity] : { hover: intensity };
      const horizontalReverseOffset = options?.horizontalOffset;
      const horizontalOffsetStyle = horizontalReverseOffset !== void 0 ? {
        paddingLeft: `${horizontalReverseOffset}px`,
        paddingRight: `${horizontalReverseOffset}px`,
        marginLeft: `${ -1 * horizontalReverseOffset}px`,
        marginRight: `${ -1 * horizontalReverseOffset}px`
      } : {};
      return {
        position: "relative",
        ...horizontalOffsetStyle,
        "&:before": {
          content: "",
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          backgroundColor: `${colors.grayDarker}`,
          borderRadius: "inherit",
          opacity: 0,
          pointerEvents: "none"
        },
        "&:hover": {
          "&:before": {
            opacity: intensities.hover
          }
        }
      };
    },
    radiusZero: (value) => {
      if (value === "left") {
        return {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0
        };
      }
      if (value === "right") {
        return {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0
        };
      }
      if (value === "bottom") {
        return {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0
        };
      }
      if (value === "top") {
        return {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0
        };
      }
      return {};
    },
    focusVisible: (value) => ({
      "@supports not selector(:focus-visible)": {
        "&:focus": value
      },
      "&:focus-visible": value
    }),
    focusVisibleBgColor: (value) => ({
      "@supports not selector(:focus-visible)": {
        "&:focus": {
          backgroundColor: value
        }
      },
      "&:focus-visible": {
        backgroundColor: value
      }
    }),
    fontFeature: (value) => {
      return value === "monospace" ? { fontFeatureSettings: `'tnum' on, 'lnum' on, 'ss06' on, 'ss08' on` } : {};
    },
    focusStyle: (variant) => {
      switch (variant) {
        case "outline":
          return { boxShadow: "0px 0px 0px 3px rgba(0, 125, 250, 0.5)" };
        case "innerOutline":
          return { boxShadow: "inset 0px 0px 0px 3px rgba(0, 125, 250, 0.5)" };
        case "solid":
          return {
            background: "rgba(36, 42, 48, 0.06)"
          };
        case "emphasize":
          return {
            boxShadow: "0px 0px 0px 3px rgba(0, 125, 250, 0.44), inset 0px 0px 0px 1px #4A94DC"
          };
      }
    },
    gapMultiColumn: (value) => {
      return {
        marginBottom: `-${value}px`,
        "& > *": {
          marginBottom: value,
          "&:not(:last-child)": {
            marginRight: value
          }
        }
      };
    },
    textEllipsis: (value) => {
      if (value === false) {
        return {};
      }
      const lineClamp = value === true ? 1 : value.lineClamp;
      return {
        display: "-webkit-box",
        overflow: "hidden",
        textOverflow: "ellipsis",
        wordBreak: "break-all",
        lineClamp,
        "-webkit-box-orient": "vertical",
        "-webkit-line-clamp": lineClamp
      };
    },
    listTheme: (value) => {
      switch (value) {
        case "unordered": {
          return {
            paddingInlineStart: "1.5em",
            ["& li::marker"]: {
              content: "\u2022  "
            }
          };
        }
      }
    }
  }
});

export { MOBILE_BREAKPOINT, SIZE_MAP, config, createTheme, css, getCssText, globalCss, keyframes, reset, styled, theme };
//# sourceMappingURL=config.mjs.map
